import * as Sentry from '@sentry/react';

import { AxiosResponse } from 'axios';
import produce from 'immer';
import { action, computed, observable } from 'mobx';
import moment from 'moment';
import React from 'react';
import {
    CART_LOCALSTORAGE_KEY,
    getStoredCart,
    ICartLocalStorageItem,
    ICartModel,
    ICartSection,
    IItemWithPrice,
    setStoredCart,
} from '../.';
import BroadBandPackage, {
    SUPER_WIFI_TITLE,
} from '@/modules/Broadband/models/BroadBandPackage';
import { mapCampaignOntoInternetProduct } from '@/modules/Broadband/store';
import { TV_DEVICE_ADDON } from '@/modules/TV/settings';
import { campaign_types } from '../../../models/campaign-shared';
import { DawaAddress } from '../../../models/dawa';
import InternetCampaign from '../../../models/internet-campaign';
import {
    FormFields,
    OrderLocalStorageItem,
    setStoredOrder,
} from '../../../models/order';
import {
    AddressInfo,
    BBAddon,
    BBInfoItem,
    InfrastructureOwner,
    PlaceBBOrderArguments,
    PlaceTVOrderArguments,
    PlaceTVVaelgSelvOrderArguments,
    SowsOrderResponse,
    TechnologyType,
    TVPackageItem,
    VaelgSelvTVChannel,
    VaelgSelvTVPackage,
} from '../../../models/sows';
import TVPackageCampaign from '../../../models/tv-package-campaign';
import TVVaelgSelvCampaign from '../../../models/tv-vaelg-selv-campaign';
import { ADDRESS_DATA_CACHE_KEY } from '../../../utilities/cookies';
import {
    APPLE_TV_PRICE,
    APPLE_TV_TITLE,
    CHROMECAST_GOOGLE_TV_PRICE,
    CHROMECAST_GOOGLE_TV_TITLE,
    CHROMECAST_PRICE,
    CHROMECAST_TITLE,
    getConfig,
    HBO_TITLE,
    STANDARD_PRICES,
    STREAMING_CAMPAIGN_PRODUCT_TITLES,
    TV_BOKS_RECURRENT_PRICE,
    TV_BOKS_TITLE,
} from '../../../utilities/settings';
import sows from '../../../utilities/sows';
import { AddressDataCacheItem } from '../../UnifiedStore';
import { getLocalStorageItemWithExpirationDate } from '../../../utilities/localStorage';
import { omit } from 'lodash';

const FORSENDELSE = 0;

interface IProductOrderState {
    [product: string]: {
        submitting: boolean;
        submitted: boolean;
        orderId: null | string;
    };
}

const EMPTY_CART: ICartModel = {
    sections: [],
    summary: {
        summaryLines: [],
        total: {
            title: `Mindstepris i 6 mdr.`,
            price: 0,
            type: 'sum',
        },
        offer: {
            title: 'Din samlede besparelse',
            price: 0,
            type: 'sum',
        },
    },
};

export class CartStore {
    constructor(
        activeInternetCampaign: campaign_types = null,
        activeTVCampaign: campaign_types = null
    ) {
        this.activeInternetCampaign = activeInternetCampaign;
        this.activeTVCampaign = activeTVCampaign;
    }

    @observable loadingDawaAddress: boolean = false;
    @observable dawaAddress: DawaAddress | null = null;
    @observable dawaAddressText: string = '';
    @observable addressInfo: AddressInfo | null = null;
    @observable isSettingNewAddress: boolean = false;

    @observable activeInternetCampaign: campaign_types = null;
    @observable activeTVCampaign: campaign_types = null;

    @observable wantsWifiTechnician: boolean = false;

    @observable
    cartData: ICartModel = EMPTY_CART;

    @action
    async initializeCart(): Promise<void> {
        this.cartData = EMPTY_CART;
        this.loadDataFromLocalStorage();
    }

    @computed
    get infraStructureOwner(): InfrastructureOwner | null {
        const addressData: AddressDataCacheItem | null = getLocalStorageItemWithExpirationDate<
            AddressDataCacheItem
        >(ADDRESS_DATA_CACHE_KEY);
        if (addressData) {
            return addressData?.data?.strategy;
        } else {
            return null;
        }
    }

    @action
    resetCart(): void {
        window.localStorage.removeItem(CART_LOCALSTORAGE_KEY);

        const storedCart: ICartLocalStorageItem | null = getStoredCart();

        if (storedCart) {
            setStoredCart({
                cartSections: storedCart.cartSections,
            });
        }

        this.dawaAddress = null;
        this.dawaAddressText = '';
        this.dawaAddress = null;
        window.location.hash = '';
    }

    @action
    private loadDataFromLocalStorage(): void {
        const storedCart: ICartLocalStorageItem | null = getStoredCart();

        if (storedCart) {
            this.cartData.sections = storedCart.cartSections;
        } else {
            console.log('No stored cart');
        }
    }

    @action
    private applyCurrentCampaignsToCartSection(
        cartSection: ICartSection
    ): ICartSection {
        return produce(cartSection, (draft) => {
            const isInternetSection = draft.type === 'Broadband';
            //
            // const tvPackageSection:
            //   | ICartSection
            //   | undefined = draft.cartSections.find(
            //   (item) => item.type === 'TVPackage'
            // );
            //
            // const tvVaelgSelvSection:
            //   | ICartSection
            //   | undefined = draft.cartSections.find(
            //   (item) => item.type === 'TVVaelgSelv'
            // );

            if (isInternetSection) {
                const campaign = new InternetCampaign(
                    cartSection.campaign || getConfig().activeInternetCampaign,
                    draft.itemDataBag.product
                );

                // Update product
                draft.items[0] = {
                    ...draft.items[0],
                    price: campaign.getRecurrent(),
                    beforePrice: campaign.getPreviousRecurrent(),
                    noOfCampaignMonths: campaign.getNumberOfCampaignMonths(),
                };

                // Update Shipment
                draft.items.find(
                    (i) => i.type === 'shipment'
                )!.price = campaign.getShipmentFee();

                // Update initial
                draft.items.find(
                    (i) => i.type === 'initial'
                )!.price = campaign.getInitialFee();
            }
            //
            // if (tvPackageSection) {
            //     const campaign = new TVPackageCampaign(
            //       getConfig().activeTVCampaign,
            //       tvPackageSection.itemDataBag.product
            //     );
            //
            //     console.log(JSON.stringify(tvPackageSection.itemDataBag));
            // }
            //
            // if (tvVaelgSelvSection) {
            //     const campaign = new TVVaelgSelvCampaign(
            //       getConfig().activeTVCampaign,
            //       tvVaelgSelvSection.itemDataBag.selectedPackage,
            //       tvVaelgSelvSection.itemDataBag.basePackage
            //
            //     );
            //
            //     // Product
            //
            //
            //     // Shipment
            //
            //     // Initial
            //
            // }
        });
    }

    @computed
    get totalRecurrent(): IItemWithPrice {
        const price = this.cartData.sections
            .map((section) => {
                return section.items
                    .filter((item) => item.type === 'recurrent')
                    .reduce((acc, item) => item.price + acc, 0);
            })
            .reduce((acc, val) => acc + val, 0);

        const beforePrice = this.cartData.sections
            .map((section) => {
                return section.items
                    .filter((item) => item.type === 'recurrent')
                    .reduce(
                        (acc, item) =>
                            (!!item.beforePrice
                                ? item.beforePrice
                                : item.price) + acc,
                        0
                    );
            })
            .reduce((acc, val) => acc + val, 0);

        return {
            title: 'Din pris pr. md.',
            price,
            beforePrice,
            type: 'recurrent',
            emphasized: true,
        };
    }

    // todo: Add test!!!
    @computed
    get priceOfOnetimeItems(): IItemWithPrice {
        return {
            type: 'onetime',
            title: 'One time items',
            price: this.cartData.sections
                .flatMap((section) =>
                    section.items
                        .filter((item) => item.type === 'onetime')
                        .map((item) => item.price)
                )
                .reduce((acc, item) => acc + item, 0),
        };
    }

    @computed
    get totalShipment(): IItemWithPrice {
        return {
            title: 'Forsendelse, i alt',
            price:
                this.infraStructureOwner === 'eniig' && this.hasTVProduct
                    ? FORSENDELSE
                    : this.cartData.sections
                          .map((section) => {
                              return section.items
                                  .filter((item) => item.type === 'shipment')
                                  .reduce((acc, item) => item.price + acc, 0);
                          })
                          .reduce((acc, val) => {
                              if (acc > 0) {
                                  return acc;
                              } else {
                                  return acc + val;
                              }
                          }, 0) > 0 // Only pay shipment once
                    ? FORSENDELSE
                    : 0,
            beforePrice:
                this.cartData.sections
                    .map((section) => {
                        return section.items
                            .filter((item) => item.type === 'shipment')
                            .reduce(
                                (acc, item) =>
                                    (!!item.beforePrice
                                        ? item.beforePrice
                                        : item.price) + acc,
                                0
                            );
                    })
                    .reduce((acc, val) => {
                        if (acc > 0) {
                            return acc;
                        } else {
                            return acc + val;
                        }
                    }, 0) > 0 // Only pay shipment once
                    ? FORSENDELSE
                    : 0,
            type: 'shipment',
        };
    }

    @computed
    get totalInitial(): IItemWithPrice {
        const price = this.cartData.sections
            .map((section) => {
                return section.items
                    .filter((item) => item.type === 'initial')
                    .reduce((acc, item) => item.price + acc, 0);
            })
            .reduce((acc, val) => acc + val, 0);

        return {
            title: 'Oprettelse, i alt',
            price,
            // beforePrice: this.cartData.sections
            //     .map((section) => {
            //         return section.items
            //             .filter((item) => item.type === 'initial')
            //             .reduce(
            //                 (acc, item) =>
            //                     (!!item.beforePrice
            //                         ? item.beforePrice
            //                         : item.price) + acc,
            //                 0
            //             );
            //     })
            //     .reduce((acc, val) => acc + val, 0),
            type: 'initial',
        };
    }

    @computed
    get totalPriceInBindingPeriod(): number {
        let totalRecurrentInBindingPeriod = 0;
        const internetProduct = this.cartData.sections.find(
            (section) => section.type === 'Broadband'
        );
        const tvPackageProduct = this.cartData.sections.find(
            (section) => section.type === 'TVPackage'
        );
        const tvVaelgSelvProduct = this.cartData.sections.find(
            (section) => section.type === 'TVVaelgSelv'
        );
        if (internetProduct) {
            const internetCampaign = new InternetCampaign(
                this.activeInternetCampaign,
                internetProduct.itemDataBag.product
            );
            totalRecurrentInBindingPeriod =
                totalRecurrentInBindingPeriod +
                internetCampaign.getTotalRecurrentInBindingPeriod();
        }

        if (tvPackageProduct) {
            const tvPackageCampaign = new TVPackageCampaign(
                this.activeTVCampaign,
                tvPackageProduct.itemDataBag.tvPackage
            );
            totalRecurrentInBindingPeriod =
                totalRecurrentInBindingPeriod +
                tvPackageCampaign.getTotalRecurrentInBindingPeriod(
                    this.hasTVBoks
                );
        }

        if (tvVaelgSelvProduct) {
            const tvVaelgSelvCampaign = new TVVaelgSelvCampaign(
                this.activeTVCampaign,
                tvVaelgSelvProduct.itemDataBag.selectedPackage,
                tvVaelgSelvProduct.itemDataBag.basePackage
            );
            totalRecurrentInBindingPeriod =
                totalRecurrentInBindingPeriod +
                tvVaelgSelvCampaign.getTotalRecurrentInBindingPeriod(
                    this.hasTVBoks
                );
        }

        const noOfQuarters = 2; // Always 6 months
        const administrationFee =
            STANDARD_PRICES.administration_quarterly * noOfQuarters;

        const summaryLines = [this.totalShipment, this.totalInitial];

        return (
            summaryLines.reduce((acc, line) => line.price + acc, 0) +
            totalRecurrentInBindingPeriod +
            this.priceOfOnetimeItems.price +
            administrationFee
        );
    }

    @computed
    get totalSavings(): number {
        return this.cartData.sections
            .map((section) => {
                return section.items.reduce((acc, item) => {
                    if (item.beforePrice && item.price < item.beforePrice) {
                        const noOfMonths = !!item.noOfCampaignMonths
                            ? item.noOfCampaignMonths
                            : 1;
                        const monthlySaving = item.beforePrice - item.price;
                        const lineTotalSaving = monthlySaving * noOfMonths;

                        return lineTotalSaving + acc;
                    } else {
                        return acc;
                    }
                }, 0);
            })
            .reduce((acc, val) => acc + val, 0);
    }

    @computed
    get cart(): ICartModel {
        let cart: ICartModel = {
            sections: [
                ...this.cartData.sections.map((s) =>
                    this.applyCurrentCampaignsToCartSection(s)
                ),
            ],
            summary: {
                summaryLines: [],
                total: EMPTY_CART.summary.total,
                offer: EMPTY_CART.summary.offer,
            },
        };

        // Total price after campaign end
        if (this.totalRecurrent.beforePrice) {
            cart.summary.summaryLines.push({
                type: 'recurrent',
                title: 'Din pris efter 1. md.',
                price: this.cartData.sections
                    .map((section) => {
                        return section.items
                            .filter((item) => item.type === 'recurrent')
                            .reduce(
                                (acc, item) =>
                                    (!!item.beforePrice &&
                                    /**
                                     * And now we get hacky, as we need to split the current/before price and show
                                     * beforePrice of main product that follows campaign end (1 mth) and addons that follow binding (6 mths)
                                     */
                                    !!item.noOfCampaignMonths &&
                                    item.noOfCampaignMonths === 1
                                        ? item.beforePrice
                                        : item.price) + acc,
                                0
                            );
                    })
                    .reduce((acc, val) => acc + val, 0),
            });
        }

        // Monthly recurrent subtotal
        // cart.summary.summaryLines.push({
        //     ...this.totalRecurrent,
        //     beforePrice: undefined, // Show as own line
        // });
        // // Monthly recurrent subtotal
        // cart.summary.summaryLines.push({
        //     ...this.totalRecurrent,
        //     beforePrice: undefined, // Show as own line
        // });

        if (
            this.totalRecurrent.beforePrice &&
            this.totalRecurrent.beforePrice !== this.totalRecurrent.price
        ) {
            cart.summary.summaryLines.push({
                type: 'sum',
                title: 'Din pris efter 6 mdr.',
                price: this.totalRecurrent.beforePrice,
            });
        }

        // Initial subtotal
        cart.summary.summaryLines.push(this.totalInitial);

        // Initial shipment subtotal
        cart.summary.summaryLines.push(this.totalShipment);

        // Total price first month
        cart.summary.summaryLines.push({
            type: 'info',
            title: 'Pris 1. md.',
            price:
                this.totalRecurrent.price +
                this.totalInitial.price +
                this.totalShipment.price +
                this.priceOfOnetimeItems.price,
        });

        // Total price in binding period - formerly 1 month
        cart.summary.total.price = this.totalPriceInBindingPeriod;

        // Offer total
        cart.summary.offer.price = this.totalSavings;

        return cart;
    }

    @computed
    get broadBandItemSection(): ICartSection | undefined {
        const foundItem =
            this.cartData &&
            this.cartData.sections &&
            this.cartData.sections.find((item) => item.type === 'Broadband');

        if (foundItem) {
            return foundItem;
        } else {
            return undefined;
        }
    }

    @computed
    get broadbandProduct(): BBInfoItem | undefined {
        const foundItem = this.broadBandItemSection;
        if (foundItem) {
            return foundItem.itemDataBag.product;
        } else {
            return undefined;
        }
    }

    @computed
    get tvPackageItem(): TVPackageItem | undefined {
        const foundItem =
            this.cartData &&
            this.cartData.sections &&
            this.cartData.sections.find((item) => item.type === 'TVPackage');

        if (foundItem) {
            return foundItem.itemDataBag.tvPackage;
        } else {
            return undefined;
        }
    }

    @computed
    get vaelgSelvItem():
        | {
              selectedPackage: VaelgSelvTVPackage;
              selectedChannels: VaelgSelvTVChannel[];
              basePackage: TVPackageItem;
          }
        | undefined {
        const foundItem =
            this.cartData &&
            this.cartData.sections &&
            this.cartData.sections.find((item) => item.type === 'TVVaelgSelv');

        if (foundItem) {
            return foundItem.itemDataBag;
        } else {
            return undefined;
        }
    }

    @computed
    get hasSuperWifi(): boolean {
        return (
            !!this.broadbandProduct &&
            !!this.broadbandProduct.addons &&
            !!this.broadbandProduct.addons.addons &&
            this.broadbandProduct.addons.addons.filter(
                (addon) => addon.name.indexOf(SUPER_WIFI_TITLE) !== -1
            ).length > 0
        );
    }

    @computed
    get noOfSuperWifiUnits(): number {
        return (
            cartStore.broadbandProduct?.addons?.addons?.filter(
                (addon) => addon.name.indexOf(SUPER_WIFI_TITLE) !== -1
            )?.length || 0
        );
    }

    @computed
    get hasBroadbandProduct(): boolean {
        return !!this.broadbandProduct;
    }

    @computed
    get hasTVPackage(): boolean {
        return !!this.tvPackageItem;
    }

    @computed
    get hasVaelgSelv(): boolean {
        return !!this.vaelgSelvItem;
    }

    @computed
    get hasTVProduct(): boolean {
        return this.hasTVPackage || this.hasVaelgSelv;
    }

    @computed
    get hasTVBoks(): boolean {
        return (
            JSON.stringify(this.cartData.sections)
                .toLowerCase()
                .indexOf(TV_BOKS_TITLE.toLowerCase()) !== -1
        );
    }

    @computed
    get hasAppleTV(): boolean {
        return (
            (
                this.cartData &&
                this.cartData.sections &&
                this.cartData.sections.filter(
                    (section) =>
                        section.items.filter((item) => {
                            return (
                                item.title
                                    .toLowerCase()
                                    .indexOf(APPLE_TV_TITLE.toLowerCase()) !==
                                -1
                            );
                        }).length > 0
                )
            ).length > 0
        );
    }

    @computed
    get hasChromecast(): boolean {
        return (
            (
                this.cartData &&
                this.cartData.sections &&
                this.cartData.sections.filter(
                    (section) =>
                        section.items.filter((item) => {
                            return (
                                item.title
                                    .toLowerCase()
                                    .indexOf(CHROMECAST_TITLE.toLowerCase()) !==
                                -1
                            );
                        }).length > 0
                )
            ).length > 0
        );
    }

    @computed
    get hasChromecastWithGoogleTV(): boolean {
        return (
            (
                this.cartData &&
                this.cartData.sections &&
                this.cartData.sections.filter(
                    (section) =>
                        section.items.filter((item) => {
                            return (
                                item.title
                                    .toLowerCase()
                                    .indexOf(
                                        CHROMECAST_GOOGLE_TV_TITLE.toLowerCase()
                                    ) !== -1
                            );
                        }).length > 0
                )
            ).length > 0
        );
    }

    @computed
    get hasTVDevice(): boolean {
        return (
            this.hasTVBoks ||
            this.hasAppleTV ||
            this.hasChromecast ||
            this.hasChromecastWithGoogleTV
        );
    }

    @action
    async addSection(newCartSection: ICartSection): Promise<void> {
        const currentCart = getStoredCart();
        let cartSections: ICartSection[] = [];
        if (currentCart && currentCart.cartSections.length > 0) {
            cartSections = currentCart.cartSections;
        }

        cartSections.push(newCartSection);

        setStoredCart({
            cartSections,
        });
        await this.initializeCart();
    }

    @action
    async addTVBoks(): Promise<void> {
        const currentCart = getStoredCart();
        let cartSections: ICartSection[] = [];
        if (currentCart && currentCart.cartSections.length > 0) {
            cartSections = currentCart.cartSections;
        }

        const existingCartSectionIndex = cartSections.findIndex(
            ({ type }) => type === 'TVPackage' || type === 'TVVaelgSelv'
        );

        if (!cartSections[existingCartSectionIndex]) {
            console.log(
                'addTVBoks: No Tv Package or VaelgSelv section in cart'
            );
            return;
        }

        if (
            cartSections[existingCartSectionIndex].items.filter(
                (item) => item.title === TV_BOKS_TITLE
            ).length > 0
        ) {
            return; // Do nothing. It's already there.
        } else {
            const newCartSection: ICartSection = produce(
                cartSections[existingCartSectionIndex],
                (draft) => {
                    draft.items.splice(1, 0, {
                        type: 'recurrent',
                        title: TV_BOKS_TITLE,
                        price: TV_BOKS_RECURRENT_PRICE,
                        priceUnit: 'md',
                    });

                    draft.items.push({
                        type: 'shipment',
                        title: `Forsendelse, ${TV_BOKS_TITLE}`,
                        price: FORSENDELSE,
                    });
                }
            );

            if (existingCartSectionIndex !== -1) {
                cartSections.splice(
                    existingCartSectionIndex,
                    1,
                    newCartSection
                );
            } else {
                cartSections.push(newCartSection);
            }

            setStoredCart({
                cartSections,
            });
            await this.initializeCart();
        }
    }

    @action
    async removeTVBoks(): Promise<void> {
        const storedCart = getStoredCart();

        const newCart: ICartLocalStorageItem | null = produce(
            storedCart,
            (draft) => {
                const tvSection = draft!.cartSections.find(
                    (section) =>
                        section.type === 'TVPackage' ||
                        section.type === 'TVVaelgSelv'
                );

                tvSection!.items = tvSection!.items.filter(
                    (item) => item.title.indexOf(TV_BOKS_TITLE) === -1
                );
            }
        );

        if (newCart) {
            setStoredCart(newCart);
            await this.initializeCart();
        }
    }

    @action
    async addTVDevices(noOfDevicesToAdd: {
        appleTV?: number;
        chromecast?: number;
        chromecastWithGoogleTV?: number;
    }): Promise<void> {
        const currentCart = getStoredCart();

        if (currentCart === null) {
            throw 'No stored cart';
        }

        let cartSections: ICartSection[] = [];
        if (currentCart && currentCart.cartSections.length > 0) {
            cartSections = currentCart.cartSections;
        }

        // Pakke eller VS
        const indexOfSectionToAddDevicesTo = cartSections.findIndex(
            ({ type }) => type === 'TVPackage' || type === 'TVVaelgSelv'
        );

        if (indexOfSectionToAddDevicesTo === -1) {
            throw 'No TVPackage or TVVaelgSelv section';
        }

        const newCartSection: ICartSection = produce(
            cartSections[indexOfSectionToAddDevicesTo],
            (draft) => {
                if (noOfDevicesToAdd.appleTV && noOfDevicesToAdd.appleTV > 0) {
                    if (
                        draft.items.filter(
                            (item) => item.title.indexOf(APPLE_TV_TITLE) !== -1
                        ).length === 0
                    ) {
                        draft.items.splice(1, 0, {
                            type: 'onetime',
                            title: APPLE_TV_TITLE,
                            price: APPLE_TV_PRICE,
                            priceUnit: 'kr.',
                        });

                        draft.items.push({
                            type: 'shipment',
                            title: `Forsendelse, ${APPLE_TV_TITLE}`,
                            price: FORSENDELSE,
                        });
                    }
                }

                if (
                    noOfDevicesToAdd.chromecast &&
                    noOfDevicesToAdd.chromecast > 0
                ) {
                    if (
                        draft.items.filter(
                            (item) =>
                                item.title.indexOf(CHROMECAST_TITLE) !== -1
                        ).length === 0
                    ) {
                        draft.items.splice(1, 0, {
                            type: 'onetime',
                            title: CHROMECAST_TITLE,
                            price: CHROMECAST_PRICE,
                            priceUnit: 'kr.',
                        });

                        draft.items.push({
                            type: 'shipment',
                            title: `Forsendelse, ${CHROMECAST_TITLE}`,
                            price: FORSENDELSE,
                        });
                    }
                }

                if (
                    noOfDevicesToAdd.chromecastWithGoogleTV &&
                    noOfDevicesToAdd.chromecastWithGoogleTV > 0
                ) {
                    if (
                        draft.items.filter(
                            (item) =>
                                item.title.indexOf(
                                    CHROMECAST_GOOGLE_TV_TITLE
                                ) !== -1
                        ).length === 0
                    ) {
                        draft.items.splice(1, 0, {
                            type: 'onetime',
                            title: CHROMECAST_GOOGLE_TV_TITLE,
                            price: CHROMECAST_GOOGLE_TV_PRICE,
                            priceUnit: 'kr.',
                        });

                        draft.items.push({
                            type: 'shipment',
                            title: `Forsendelse, ${CHROMECAST_GOOGLE_TV_TITLE}`,
                            price: FORSENDELSE,
                        });
                    }
                }
            }
        );

        if (indexOfSectionToAddDevicesTo !== -1) {
            cartSections.splice(
                indexOfSectionToAddDevicesTo,
                1,
                newCartSection
            );
        }

        setStoredCart({ cartSections });
        await this.initializeCart();
    }

    @action
    async removeTVDevice(type: TV_DEVICE_ADDON): Promise<boolean> {
        const storedCart = getStoredCart();

        if (storedCart === null) {
            return false;
        }

        const newCart: ICartLocalStorageItem | null = produce(
            storedCart,
            (draft) => {
                const section = draft.cartSections.find(
                    (section) =>
                        section.type === 'TVPackage' ||
                        section.type === 'TVVaelgSelv'
                );

                const titleOfItemToRemove =
                    type === 'appletv'
                        ? APPLE_TV_TITLE
                        : type === 'chromecast'
                        ? CHROMECAST_TITLE
                        : CHROMECAST_GOOGLE_TV_TITLE;

                if (section) {
                    section.items = section.items.filter(
                        (item) =>
                            item.title
                                .toLowerCase()
                                .indexOf(titleOfItemToRemove.toLowerCase()) ===
                            -1
                    );
                }
            }
        );

        if (newCart) {
            setStoredCart(newCart);
            await this.initializeCart();
        }

        return true;
    }

    @action
    async removeStreamingCampaignAddons(): Promise<void> {
        const storedCart = getStoredCart();

        const newCart: ICartLocalStorageItem | null = produce(
            storedCart,
            (draft) => {
                const bbSection = draft!.cartSections.find(
                    (s) => s.type === 'Broadband'
                );

                bbSection!.items = bbSection!.items.filter(
                    (item) =>
                        !(
                            item.title ===
                                STREAMING_CAMPAIGN_PRODUCT_TITLES.TV2PLAY ||
                            item.title ===
                                STREAMING_CAMPAIGN_PRODUCT_TITLES.HBOMAX
                        )
                );

                bbSection!.itemDataBag.product.addons.addons = bbSection?.itemDataBag.product.addons.addons.filter(
                    (addon: BBAddon) => {
                        if (
                            addon.name.indexOf(
                                STREAMING_CAMPAIGN_PRODUCT_TITLES.TV2PLAY
                            ) !== -1
                        ) {
                            return false;
                        } else if (
                            addon.name.indexOf(
                                STREAMING_CAMPAIGN_PRODUCT_TITLES.HBOMAX
                            ) !== -1
                        ) {
                            return false;
                        } else {
                            return true;
                        }
                    }
                );
            }
        );

        if (newCart) {
            setStoredCart(newCart);
            await this.initializeCart();
        }
    }

    @action
    async removeAppleTV(): Promise<boolean> {
        return this.removeTVDevice('appletv');
    }

    @action
    async removeChromecast(): Promise<boolean> {
        return this.removeTVDevice('chromecast');
    }

    @action
    async removeChromecastWithGoogleTV(): Promise<boolean> {
        return this.removeTVDevice('chromecast_google_tv');
    }

    @action
    async addSuperWifi(noOfUnits: number): Promise<boolean> {
        const storedCart = getStoredCart();

        if (storedCart === null) {
            return false;
        }

        const newCart: ICartLocalStorageItem | null = produce(
            storedCart,
            (draft) => {
                const broadBandSection = draft!.cartSections.find(
                    (section) => section.type === 'Broadband'
                );

                const product = broadBandSection!.itemDataBag
                    .product as BBInfoItem;

                const broadbandPackage = new BroadBandPackage(product);

                broadBandSection!.items.push({
                    type: 'recurrent',
                    title: 'SuperWifi',
                    description: `${noOfUnits} enhed${
                        noOfUnits === 1 ? '' : 'er'
                    }`,
                    price: broadbandPackage.getPriceOfSuperWifi(noOfUnits),
                    priceUnit: 'md.',
                });

                broadBandSection!.items.push({
                    type: 'shipment',
                    title: `Forsendelse, ${SUPER_WIFI_TITLE}`,
                    price: FORSENDELSE,
                });

                if (!product.addons.addons) {
                    product.addons.addons = [];
                }

                for (let i = 0; i < noOfUnits; i++) {
                    const order_code = broadbandPackage.getOrderCodeOfSuperWifiUnitAtIndex(
                        i
                    );

                    product.addons.addons.push({
                        type: '',
                        description: '',
                        price: {
                            initial: null,
                            recurrent: broadbandPackage.getPriceOfSuperWifiUnitAtIndex(
                                i
                            ),
                        },
                        name: `${SUPER_WIFI_TITLE} - ${order_code}`,
                        order_code,
                    });
                }

                // Update value of total price
                product.total.recurrent =
                    product.total.recurrent +
                    product.addons.addons.reduce((acc, item) => {
                        return acc + item.price.recurrent;
                    }, 0);
            }
        );

        if (newCart) {
            setStoredCart(newCart);
            await this.initializeCart();
        }

        return true;
    }

    @action
    async removeSuperWifi(): Promise<boolean> {
        const storedCart = getStoredCart();

        if (storedCart === null) {
            return false;
        }

        const newCart: ICartLocalStorageItem | null = produce(
            storedCart,
            (draft) => {
                const broadBandSection = draft!.cartSections.find(
                    (section) => section.type === 'Broadband'
                );

                if (broadBandSection) {
                    const product = broadBandSection!.itemDataBag
                        .product as BBInfoItem;

                    if (product) {
                        // Subtract superwifi price from total before we remove them from addons
                        product.total.recurrent =
                            product.total.recurrent -
                            product.addons.addons.reduce((acc, item) => {
                                return acc + item.price.recurrent;
                            }, 0);

                        product.addons.addons = product.addons.addons.filter(
                            (addon) =>
                                addon.name.indexOf(SUPER_WIFI_TITLE) === -1
                        );
                    }

                    if (broadBandSection.items) {
                        broadBandSection.items = broadBandSection!.items.filter(
                            (item) =>
                                item.title.indexOf(SUPER_WIFI_TITLE) === -1
                        );
                    }
                }
            }
        );

        if (newCart) {
            setStoredCart(newCart);
            await this.initializeCart();
        }

        return true;
    }

    @action
    async setWantsWifiTechnician(wantsWifiTechnician: boolean): Promise<void> {
        cartStore.wantsWifiTechnician = wantsWifiTechnician;
        if (cartStore.wantsWifiTechnician) {
            // Make sure there's maximum 2 super wifi units in the cart
            const currentNoOfSuperWifiUnits = cartStore.noOfSuperWifiUnits;
            if (currentNoOfSuperWifiUnits > 0) {
                await cartStore.removeSuperWifi();
                await cartStore.addSuperWifi(
                    currentNoOfSuperWifiUnits > 2
                        ? 2
                        : currentNoOfSuperWifiUnits
                );
            }
        }
    }

    @action
    removeBroadband(): void {
        const currentCart = getStoredCart();
        let updatedCartSections: ICartSection[] = [];

        if (currentCart) {
            updatedCartSections = currentCart.cartSections.filter(
                (section) => section.type !== 'Broadband'
            );
            setStoredCart({
                ...currentCart,
                cartSections: updatedCartSections,
            });
            this.loadDataFromLocalStorage();
        } else {
            console.log('CartStore: removeBroadband – no current cart');
        }
    }

    @action
    removeVaelgSelv(): void {
        const currentCart = getStoredCart();
        let updatedCartSections: ICartSection[] = [];

        if (currentCart) {
            updatedCartSections = currentCart.cartSections.filter(
                (section) => section.type !== 'TVVaelgSelv'
            );

            setStoredCart({
                ...currentCart,
                cartSections: updatedCartSections,
            });
            this.loadDataFromLocalStorage();
        } else {
            console.log('CartStore: removeVaelgSelv – no current cart');
        }
    }

    @action
    removeTVPackage(): void {
        const currentCart = getStoredCart();
        let updatedCartSections: ICartSection[] = [];

        if (currentCart) {
            updatedCartSections = currentCart.cartSections.filter(
                (section) => section.type !== 'TVPackage'
            );

            setStoredCart({
                ...currentCart,
                cartSections: updatedCartSections,
            });
            this.loadDataFromLocalStorage();
        } else {
            console.log('CartStore: removeTVPackage – no current cart');
        }
    }

    @observable
    orderState: IProductOrderState = {
        broadband: {
            submitting: false,
            submitted: false,
            orderId: null,
        },
        TVPackage: {
            submitting: false,
            submitted: false,
            orderId: null,
        },
        TVVaelgSelv: {
            submitting: false,
            submitted: false,
            orderId: null,
        },
    };

    async submitBroadband(
        strategy: InfrastructureOwner,
        dawaAddress: DawaAddress,
        technologyType: TechnologyType,
        formValues: FormFields,
        section: ICartSection
    ): Promise<SowsOrderResponse> {
        const productInfo = section.itemDataBag.product as BBInfoItem;
        const campaign_type: campaign_types =
            section.campaign || getConfig().activeInternetCampaign;

        const ETRACK_TEST = '84433';

        try {
            // Log for debugging
            sows.post('/orderDataSent', {
                dawa_id: dawaAddress.id,
                strategy,
                technologyFromAddressData: technologyType,
            });
        } catch (e) {
            console.log(e);
        }

        const orderObject: PlaceBBOrderArguments = {
            // etrack_category: ETRACK_TEST,
            strategy,
            dawa_id: dawaAddress.id,
            product_info: mapCampaignOntoInternetProduct(
                productInfo,
                campaign_type
            ),
            cpr: formValues.cpr,
            order_form_info_values: formValues,
            start_date: moment(formValues.selectedStartDate).unix(),
            give_permissions: formValues.give_permissions,
            technology_type: strategy === 'stofa' ? technologyType : 'fiber', // This???
        };

        const response: AxiosResponse<SowsOrderResponse> = await sows.post(
            `/placeBBOrder`,
            orderObject
        );
        return response.data;
    }

    private getTVDevice(section: ICartSection): string | undefined {
        return section.items.filter(
            (item) => item.title.indexOf(CHROMECAST_TITLE) !== -1
        ).length > 0
            ? CHROMECAST_TITLE
            : section.items.filter(
                  (item) =>
                      item.title.indexOf(CHROMECAST_GOOGLE_TV_TITLE) !== -1
              ).length > 0
            ? CHROMECAST_GOOGLE_TV_TITLE
            : section.items.filter(
                  (item) => item.title.indexOf(APPLE_TV_TITLE) !== -1
              ).length > 0
            ? APPLE_TV_TITLE
            : undefined;
    }

    async submitTVVaelgSelv(
        strategy: InfrastructureOwner,
        dawaAddress: DawaAddress,
        technologyType: TechnologyType,
        formValues: FormFields,
        section: ICartSection
    ): Promise<SowsOrderResponse> {
        const productInfo = section.itemDataBag as {
            selectedPackage: VaelgSelvTVPackage;
            selectedChannels: VaelgSelvTVChannel[];
            basePackage: TVPackageItem;
        };
        const response: AxiosResponse<SowsOrderResponse> = await sows.post(
            `/placeTVVaelgSelvOrder`,
            {
                strategy,
                dawa_id: dawaAddress.id,
                order_form_info_values: formValues,
                cpr: formValues.cpr,
                start_date: moment(formValues.selectedStartDate).unix(),
                give_permissions: formValues.give_permissions,
                selectedPackage: productInfo.selectedPackage,
                selectedChannels: productInfo.selectedChannels,
                basePackage: productInfo.basePackage,
                technology_type: technologyType,
                include_hbo:
                    JSON.stringify(this.cart.sections).indexOf(HBO_TITLE) !==
                    -1,
                tvDevice: this.getTVDevice(section),
                include_tv_boks: this.hasTVBoks,
            } as PlaceTVVaelgSelvOrderArguments
        );
        return response.data;
    }

    async submitTVPackage(
        strategy: InfrastructureOwner,
        dawaAddress: DawaAddress,
        technologyType: TechnologyType,
        formValues: FormFields,
        section: ICartSection
    ): Promise<SowsOrderResponse> {
        const productInfo = section.itemDataBag as { tvPackage: TVPackageItem };

        const include_tv_boks =
            section.items.filter((item) => item.title === TV_BOKS_TITLE)
                .length > 0;

        const response: AxiosResponse<SowsOrderResponse> = await sows.post(
            `/placeTVOrder`,
            {
                strategy,
                dawa_id: dawaAddress.id,
                cpr: formValues.cpr,
                technology_type: technologyType,
                order_form_info_values: formValues,
                start_date: moment(formValues.selectedStartDate).unix(),
                give_permissions: formValues.give_permissions,
                product_info: productInfo.tvPackage,
                tvDevice: this.getTVDevice(section),
                include_tv_boks,
            } as PlaceTVOrderArguments
        );
        return response.data;
    }

    @action
    async submitOrders(
        dawaAddress: DawaAddress,
        technologyType: TechnologyType,
        strategy: InfrastructureOwner,
        formFields: FormFields
    ): Promise<boolean> {
        Sentry.setTag('method', 'submitOrders');

        const formFieldsWithoutCPR = omit(formFields, ['cpr']);

        if (dawaAddress && technologyType && this.cart.sections.length > 0) {
            let orderToStore: OrderLocalStorageItem = {
                formFields: formFieldsWithoutCPR,
                serializedCart: JSON.stringify(this.cart),
                expires: moment().add(1, 'day').toDate(),
                dawa_address_text: this.dawaAddressText,
                payloads: {},
                responses: {},
            };

            await Promise.all(
                this.cart.sections.map(async (section) => {
                    if (section.type === 'Broadband') {
                        this.orderState.broadband.submitting = true;

                        orderToStore.payloads.BB = {
                            strategy,
                            product_info: mapCampaignOntoInternetProduct(
                                section.itemDataBag.product as BBInfoItem,
                                section.campaign ||
                                    getConfig().activeInternetCampaign
                            ),
                            dawa_id: dawaAddress.id,
                            give_permissions: formFields.give_permissions,
                            order_form_info_values: formFieldsWithoutCPR,
                            start_date: moment(
                                formFields.selectedStartDate
                            ).unix(),
                            technology_type: technologyType,
                        };

                        const response = await this.submitBroadband(
                            strategy,
                            dawaAddress,
                            technologyType,
                            formFields,
                            section
                        );
                        this.orderState.broadband.orderId = response.order_id;
                        this.orderState.broadband.submitted = true;
                        this.orderState.broadband.submitting = false;

                        orderToStore.responses.BB = response;

                        this.removeBroadband();
                    }
                    if (section.type === 'TVVaelgSelv') {
                        this.orderState.TVVaelgSelv.submitting = true;

                        const rawData = section.itemDataBag as PlaceTVVaelgSelvOrderArguments;

                        orderToStore.payloads.TVVaelgSelv = {
                            strategy,
                            basePackage: rawData.basePackage,
                            selectedChannels: rawData.selectedChannels,
                            selectedPackage: rawData.selectedPackage,
                            technology_type: rawData.technology_type,
                            dawa_id: dawaAddress.id,
                            give_permissions: formFields.give_permissions,
                            order_form_info_values: formFieldsWithoutCPR,
                            start_date: moment(
                                formFields.selectedStartDate
                            ).unix(),
                        };

                        const response = await this.submitTVVaelgSelv(
                            strategy,
                            dawaAddress,
                            technologyType,
                            formFields,
                            section
                        );
                        this.orderState.TVVaelgSelv.orderId = response.order_id;
                        this.orderState.TVVaelgSelv.submitted = true;
                        this.orderState.TVVaelgSelv.submitting = false;

                        orderToStore.responses.TVVaelgSelv = response;

                        this.removeVaelgSelv();
                    }
                    if (section.type === 'TVPackage') {
                        this.orderState.TVPackage.submitting = true;

                        const include_tv_boks =
                            section.items.filter(
                                (item) => item.title === TV_BOKS_TITLE
                            ).length > 0;

                        orderToStore.payloads.TVPackage = {
                            strategy,
                            dawa_id: dawaAddress.id,
                            technology_type: technologyType,
                            order_form_info_values: formFieldsWithoutCPR,
                            start_date: moment(
                                formFields.selectedStartDate
                            ).unix(),
                            give_permissions: formFields.give_permissions,
                            product_info: section.itemDataBag.tvPackage,
                            include_tv_boks,
                        } as PlaceTVOrderArguments;

                        const response = await this.submitTVPackage(
                            strategy,
                            dawaAddress,
                            technologyType,
                            formFields,
                            section
                        );
                        this.orderState.TVPackage.orderId = response.order_id;
                        this.orderState.TVPackage.submitted = true;
                        this.orderState.TVPackage.submitting = false;

                        orderToStore.responses.TVPackage = response;

                        this.removeTVPackage();
                    }
                })
            );

            setStoredOrder(orderToStore);
            return true;
        } else {
            throw new Error('Missing info needed to submit orders');
        }
    }
}

export const cartStore = new CartStore(
    getConfig().activeInternetCampaign,
    getConfig().activeTVCampaign
);
export const cartStoreContext = React.createContext(cartStore);
const useCartStore = () => React.useContext(cartStoreContext);
export default useCartStore;
